<template>
  <div class="associated-object-model">
    <div class="container">
      <template v-if="index > 0">
        <br>
        <row>
          <column class="sz100">
            <and-or-select v-model="ruleSetsRoot.join_type" outside-modal></and-or-select>
          </column>
        </row>
      </template>

      <div class="form-element align-center rule-set">
        <row>
          <column>
            <a-select
              :id="`select_object_model_association_${index}`"
              :options="objectModelSearchOptions"
              @input="selectObjectModel"
              :value="objectModelAssociation.object_model_client_id"
              bind-by-object
              placeholder="Select object model..."
            ></a-select>
          </column>
          <column v-if="isUserType">
            <a-select
              :id="`select_account_relationships_${index}`"
              class="rule"
              :options="accountRelationVOptionSet"
              v-model="objectModelAssociation.account_relation_client_id"
              placeholder="Select one..."
            ></a-select>
          </column>
          <column v-if="isEntityType">
            <a-select
              :id="`select_entity_account_relationships_${index}`"
              class="rule"
              :options="entityAccountRelationVOptionSet"
              v-model="objectModelAssociation.entity_account_relation_client_id"
              placeholder="Select one..."
            ></a-select>
          </column>
          <column v-if="includeRelativeTimeCondition">
            <a-select
              v-if="objectModelAssociation.object_model_client_id"
              :id="`select_object_model_prerequisite_type_${index}`"
              :options="objectModelPrereqOptions"
              v-model="objectModelAssociation.prerequisite_type"
            ></a-select>
          </column>

              <div class="dropdown pt-2 pl-2">
                <button
                  class="dropdown-toggle btn-icon clear-area controllers"
                  @click="openContextMenu"
                >
                  <icon class="kebab"></icon>
                  <span class="sr-only">More Actions Dropdown</span>
                </button>
              </div>

        </row>

        <template v-if="objectModelAssociation.object_model_client_id && (includeRelativeTimeCondition && objectModelAssociation.prerequisite_type === objectModelPrerequisiteType.ConditionalRule || !includeRelativeTimeCondition)">
          <div :id="`object_model_association_conditional_rule_sets_${index}`">
            <edit-conditional-rule-set
              v-if="ruleSets.length > 0"
              :parent="objectModelAssociation"
              :ruleSet="ruleSets[0]"
              outside-modal
            ></edit-conditional-rule-set>
          </div>
        </template>

        <template v-if="includeRelativeTimeCondition && objectModelAssociation.object_model_client_id && objectModelAssociation.prerequisite_type === objectModelPrerequisiteType.RelativeDate">
          <configure-relative-time-component
            id="relative_time_period"
            additional-class="layout"
            v-model="objectModelAssociation.relative_time_period"
            prop_key="relative_time_period"
            :model="objectModelAssociation"
            :unitOptions="DurationAndTimeHelper.durationUnitLongOptions"
            :options="[{label: 'Relative time interval', value: DurationType.propertyBasedTimeSpan,
                    sectionConfig:
                      {sectionLabel: '',
                      includeWindowComparisonOptions: true,
                      dropdownOptions: relativeTimeIntervalPropertyBasedOptions }
                    }]">
          </configure-relative-time-component>
        </template>

        <row v-if="enableCustomErrorMessages && objectModelSelected" class="text-left">
          <column>
            <label :for="`${objectModelAssociation.object_model_client_id}-error-message-${index}`">Custom Error Message</label>
            <wysiwyg  :id="`${objectModelAssociation.object_model_client_id}-error-message-${index}`"
                      :name="`${objectModelAssociation.object_model_client_id}-error-message-${index}`"
                      v-model="objectModelAssociation.error_message"
                      :config-options="wysiwygConfig"
            ></wysiwyg>
          </column>
        </row>

      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  // Components
  import andOrSelect from "~/components/conditionalRuleBuilder/vSelects/andOrSelect.vue";
  import EditConditionalRuleSet from "~/components/conditionalRuleBuilder/editConditionalRuleSet.vue";
  import ConfigureRelativeTimeComponent from "~/components/configuration/configure-relative-time-component.vue"
  import Wysiwyg from "~/components/wysiwyg/wysiwyg.vue";
  // Interfaces
  import {IVOptionSetEndpoint, IVOptionSetLocal} from "~/interfaces/vSelectInterfaces";
  import IConditionalRuleContainer from "~/models/conditionalRules/conditionalRuleContainer";
  // Models
  import {ConditionalFieldDefinition} from "~/models/conditionalRules/conditionalFieldDefinition";
  import ObjectModel from "~/models/objectModel";
  import ConditionalRuleSetFactory from "~/models/conditionalRules/conditionalRuleSetFactory";
  import ObjectModelConditionalRuleSet from "~/models/objectModels/objectModelConditionalRuleSet";
  // Enums
  import {Route} from "~/enums/route";
  import {JoinType} from "~/models/conditionalRules/joinType";
  // Helpers
  import {RouteHelper} from "~/helpers/routeHelper";
  import {EnumHelper} from "~/helpers/enumHelper";
  import {objectModelPrerequisiteType, objectModelPrerequisiteTypeName} from "~/enums/objectModelPrerequisiteType";
  import {DynamicReferenceType} from "~/enums/dynamicReferenceType";
  import {
    ObjectModelBuiltinPropertyType,
    ObjectModelBuiltinPropertyTypeName
  } from "~/enums/objectModelBuiltinPropertyType";
  import DurationAndTimeHelper from "~/helpers/durationAndTimeHelper";
  import {DurationType} from "~/enums/durationType";
  import {ObjectModelRelationType} from "~/enums/objectModelRelationType";
  import {PermitteeType} from "~/enums/permitteeType";
  import {TagColor} from "~/enums/tagColor";

  interface IObjectModelAssociation extends IConditionalRuleContainer {
      object_model_client_id: string;
      object_model: ObjectModel;
      error_message: string;
    }

  export default Vue.extend({
    components: {andOrSelect, EditConditionalRuleSet, ConfigureRelativeTimeComponent, Wysiwyg},
    props: {
      objectModelAssociation: {
        type: Object as () => IObjectModelAssociation,
        required: true,
      },
      includeRelativeTimeCondition: {
        type: Boolean,
        default: false
      },
      ruleSetsRoot: {
        type: Object as () => ({join_type: JoinType}),
        required: true,
      },
      index: {
        type: Number,
        default: null
      },
      fieldDefinition: {
        type: Object as () => ConditionalFieldDefinition,
        required: true,
      },
      enableCustomErrorMessages: {
        type: Boolean,
        default: false
      },
      isUserType: {
        type: Boolean,
        default:false
      },
      isEntityType: {
        type: Boolean,
        default: false
      }
    },
    data:function () {
      return {
        objectModelPrerequisiteType,
        DurationAndTimeHelper,
        DurationType
      };
    },
    async beforeMount() {
      if (this.objectModelAssociation.object_model_client_id) {
        const resp = await ObjectModel.provider.show(this.objectModelAssociation.object_model);
        new ObjectModel().loadFromJson(resp[0].object_model);
      }
    },
    computed: {
      objectModelSearchOptions(): IVOptionSetEndpoint {
        return {
          valueKey: "id",
          labelKey: "title",
          endpoint: RouteHelper.path({ route: Route.searchObjectModels }),
          rootKey: "object_models",
          selectedOption: this.objectModelAssociation.object_model
        };
      },
      ruleSets(): ObjectModelConditionalRuleSet[] {
        return this.objectModelAssociation.getSortedConditionalRuleSets(this.fieldDefinition.fieldName);
      },
      objectModelPrereqOptions(): IVOptionSetLocal {
        return {
          valueKey: "value",
          labelKey: "label_title",
          options: EnumHelper.toArray(objectModelPrerequisiteType).map(x=> {return  {value: x, label_title: objectModelPrerequisiteTypeName[x]}}),
          selectedOption: this.objectModelAssociation.object_model
        };
      },

      relativeTimeIntervalPropertyBasedOptions(): IVOptionSetLocal {
        let options = this.objectModelAssociation.object_model.dateProperties(false, true).map(x => {
          return {
            label: x.label_title,
            value: x.client_id,
            type: DynamicReferenceType.ObjectModelProperty
          };
        });

        options.push({
          label: ObjectModelBuiltinPropertyTypeName[ObjectModelBuiltinPropertyType.CreatedAt],
          value: ObjectModelBuiltinPropertyType.CreatedAt,
          type: DynamicReferenceType.ObjectModelRecordMetadata,
        });

        // this implementation will probably cause problems w/ a-select if `value` ever overlaps across different types
        return options;
      },
      wysiwygConfig() {
        return {
          toolbarButtons: [
            'paragraphFormat',
            '|',
            'bold',
            'italic',
            'underline',
            '|',
            'insertTable',
            'formatOL',
            'formatUL',
            '|',
            'indent',
            'outdent',
            'insertLink',
          ]
        }
      },
      objectModelSelected(): boolean {
        return this.objectModelAssociation.object_model_client_id?.length > 0;
      },
      accountRelationVOptionSet: {
        get(): IVOptionSetLocal[] {
          return [
            this.recordHolderVOptionSet,
            this.accountRelationsVOptionSet,
          ];
        }
      },
      entityAccountRelationVOptionSet: {
        get(): IVOptionSetLocal[] {
          return [
            this.recordHolderVOptionSet,
            this.entityAccountRelationsVOptionSet,
          ];
        }
      },
      recordHolderVOptionSet: {
        get(): IVOptionSetLocal {
          return {
            optionType: this.isUserType ? PermitteeType.objectOwner : PermitteeType.entityOwner,
            options: this.userTypeOrEntityTypeOptions,
            isPinned: true,
            selectedColor: this.isUserType ? TagColor[PermitteeType.objectOwner] : TagColor[PermitteeType.entityOwner],
          };
        }
      },
      accountRelationsVOptionSet: {
        get(): IVOptionSetLocal {
          const options = [];
          let accountRelations = this.objectModelAssociation.object_model ? this.objectModelAssociation.object_model.objectModelRelationsArray.filter(r => r.relation_type === ObjectModelRelationType.Account) : [];
          for (const accountRelation of accountRelations) {
            options.push({label: accountRelation.label_title || 'untitled account', client_id: accountRelation.client_id});
          }

          return {
            optionType: PermitteeType.accountRelation,
            valueKey: 'client_id',
            heading: 'Accounts',
            options,
            selectedColor: TagColor[PermitteeType.accountRelation],
          }
        }
      },
      entityAccountRelationsVOptionSet: {
        get(): IVOptionSetLocal {
          const options = [];
          let accountRelations = this.objectModelAssociation.object_model ? this.objectModelAssociation.object_model.objectModelRelationsArray.filter(r => r.relation_type === ObjectModelRelationType.EntityAccount) : [];
          for (const accountRelation of accountRelations) {
            options.push({label: accountRelation.label_title || 'untitled entity account', client_id: accountRelation.client_id});
          }

          return {
            optionType: PermitteeType.entityAccountRelation,
            valueKey: 'client_id',
            heading: 'Entity Accounts',
            options,
            selectedColor: TagColor[PermitteeType.entityAccountRelation],
          }
        }
      },
      userTypeOrEntityTypeOptions() {
        if (this.isUserType) {
          return [{label: 'Record Holder', value: PermitteeType.objectOwner.toString()}]
        } else {
          return [{label: 'Record Holder', value: PermitteeType.entityOwner.toString()}]
        }
      },
    },
    methods: {
      async selectObjectModel(objectModel: ObjectModel) {
        if (this.objectModelAssociation.object_model_client_id) {
          for (let ruleSet of this.ruleSets) {
            ruleSet.destroy();
          }
        }
        this.objectModelAssociation.object_model_client_id = objectModel.client_id;
        const resp = await ObjectModel.provider.show(objectModel);

        if (!ObjectModel.repo.find(resp[0].object_model.client_id)) {
          new ObjectModel().loadFromJson(resp[0].object_model);
        }

        this.objectModelAssociation.object_model_client_id = objectModel.client_id;
        const editedRuleSet = ConditionalRuleSetFactory.createForContainer(
          this.objectModelAssociation,
          this.fieldDefinition,
          {},
          false,
          true
        );
        editedRuleSet.attachToParent(this.objectModelAssociation);
      },
      openContextMenu(event: MouseEvent) {
        this.$emit("open:context-menu", event, this.objectModelAssociation);
      }
    }
  });
</script>

